import { useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { fetchGallery } from "../../api/public.api.js";
import { assetUrl } from "../../api/client.js";
import { Container } from "../../components/layout/Container.jsx";
import { PageHero } from "../../components/shared/PageHero.jsx";
import { ImageWithSkeleton } from "../../components/shared/ImageWithSkeleton.jsx";
import { Lightbox } from "../../components/designs/Lightbox.jsx";
import { SEO } from "../../lib/seo.jsx";
import { cn } from "../../lib/cn.js";

/**
 * /gallery — admin-curated photography, split into two sections:
 * Interior and Exterior. Single tab strip across the top, lightbox on
 * click. No design tabs, no nested categories — keeps the experience
 * exactly as the brief described: "a simple gallery design, not tied
 * to home designs".
 */
const SECTIONS = [
  { key: "interior", label: "Interior" },
  { key: "exterior", label: "Exterior" },
];

export default function GalleryPage() {
  const { data, isLoading } = useQuery({
    queryKey: ["public", "gallery", "all"],
    queryFn: () => fetchGallery({ limit: 500 }),
    staleTime: 5 * 60_000,
  });

  const items = useMemo(() => (data ?? []).filter((g) => g.image), [data]);

  const counts = useMemo(() => {
    const out = { interior: 0, exterior: 0 };
    for (const g of items) {
      if (g.section === "interior") out.interior += 1;
      else if (g.section === "exterior") out.exterior += 1;
    }
    return out;
  }, [items]);

  // Default to the Interior tab — every visitor lands on the interior
  // grid first; tab strip lets them swap to exterior if they want.
  const [activeTab, setActiveTab] = useState("interior");
  const [lightbox, setLightbox] = useState({ images: [], index: -1 });

  // Deep-link support: /gallery#interior or /gallery#exterior preselects
  // the matching tab. Keeps the homepage CTAs landing in the right spot.
  const location = useLocation();
  useEffect(() => {
    const h = (location.hash || "").replace("#", "").toLowerCase();
    if (h === "interior" || h === "exterior") {
      setActiveTab(h);
    }
  }, [location.hash]);

  const tiles = useMemo(
    () => items.filter((g) => g.section === activeTab),
    [items, activeTab]
  );

  const openLightbox = (i) => {
    setLightbox({ images: tiles.map((t) => t.image), index: i });
  };

  return (
    <>
      <SEO
        title="Gallery"
        description="Browse interior and exterior photography from Shere-e-Punjab Homes — every kitchen, living space, and façade in one place."
      />

      <PageHero
        eyebrow="Gallery"
        title="Interior & exterior photography"
        description="Switch between interior and exterior shots to see how a Shere-e-Punjab home comes together."
      />

      <Container className="py-10 lg:py-14">
        {isLoading ? (
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="aspect-[4/3] animate-pulse rounded-xl bg-bg-elevated" />
            ))}
          </div>
        ) : items.length === 0 ? (
          <p className="rounded-2xl border border-dashed border-border-subtle bg-bg-elevated px-6 py-16 text-center text-text-secondary">
            Gallery coming soon — once the admin uploads a photo, it will appear here.
          </p>
        ) : (
          <>
            <div className="-mx-4 overflow-x-auto px-4">
              <div className="flex min-w-max gap-2 border-b border-border-subtle pb-3">
                {SECTIONS.map((s) => (
                  <TabChip
                    key={s.key}
                    label={s.label}
                    count={counts[s.key]}
                    active={activeTab === s.key}
                    onClick={() => setActiveTab(s.key)}
                  />
                ))}
              </div>
            </div>

            <p className="mt-6 text-sm text-text-muted">
              {tiles.length} image{tiles.length === 1 ? "" : "s"} ·{" "}
              {SECTIONS.find((s) => s.key === activeTab)?.label}
            </p>

            <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {tiles.map((g, i) => (
                <button
                  key={g._id}
                  type="button"
                  onClick={() => openLightbox(i)}
                  className="group relative aspect-[4/3] overflow-hidden rounded-xl border border-border-subtle bg-bg-elevated"
                >
                  <ImageWithSkeleton
                    src={assetUrl(g.image)}
                    alt={g.title}
                    className="absolute inset-0 h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                  />
                </button>
              ))}
            </div>
          </>
        )}
      </Container>

      {lightbox.index >= 0 && (
        <Lightbox
          images={lightbox.images}
          index={lightbox.index}
          onChange={(i) => setLightbox((s) => ({ ...s, index: i }))}
          onClose={() => setLightbox({ images: [], index: -1 })}
        />
      )}
    </>
  );
}

function TabChip({ label, count, active, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "inline-flex shrink-0 items-center gap-2 rounded-full border px-4 py-2 text-sm transition-colors",
        active
          ? "border-accent bg-accent/15 text-accent"
          : "border-border-subtle bg-bg-elevated text-text-secondary hover:border-border hover:text-text-primary"
      )}
    >
      <span>{label}</span>
      <span
        className={cn(
          "rounded-full px-2 py-0.5 text-[10px] font-medium",
          active ? "bg-accent/25 text-accent" : "bg-bg-base/60 text-text-muted"
        )}
      >
        {count}
      </span>
    </button>
  );
}
